import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Eye,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Twitter,
  Mail,
  Calendar as CalendarIcon } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const platformColors = {
  instagram: 'bg-pink-100 text-pink-600 border-pink-200',
  facebook: 'bg-blue-100 text-blue-600 border-blue-200',
  linkedin: 'bg-sky-100 text-sky-600 border-sky-200',
  youtube: 'bg-red-100 text-red-600 border-red-200',
  x: 'bg-gray-100 text-gray-700 border-gray-200',
  email: 'bg-emerald-100 text-emerald-600 border-emerald-200'
};

// Mock scheduled posts for demo
const mockScheduledPosts = [
{ id: 1, title: 'Product Launch Announcement', platform: 'instagram', date: new Date(2025, 0, 15, 10, 0), status: 'scheduled' },
{ id: 2, title: 'Weekly Newsletter', platform: 'email', date: new Date(2025, 0, 16, 9, 0), status: 'scheduled' },
{ id: 3, title: 'Behind the Scenes', platform: 'facebook', date: new Date(2025, 0, 17, 14, 0), status: 'scheduled' },
{ id: 4, title: 'Tutorial Video', platform: 'youtube', date: new Date(2025, 0, 18, 12, 0), status: 'draft' },
{ id: 5, title: 'Industry Insights', platform: 'linkedin', date: new Date(2025, 0, 20, 11, 0), status: 'scheduled' }];


export const Scheduler = () => {const { t } = useTranslation("cns");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [scheduledPosts, setScheduledPosts] = useState(mockScheduledPosts);
  const [view, setView] = useState('month'); // month, week, day

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const getPostsForDate = (date) => {
    return scheduledPosts.filter((post) =>
    post.date.toDateString() === date.toDateString()
    );
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const PlatformIcon = ({ platform }) => {const { t } = useTranslation("cns");
    switch (platform) {
      case 'instagram':return <Instagram className="h-3 w-3" />;
      case 'facebook':return <Facebook className="h-3 w-3" />;
      case 'linkedin':return <Linkedin className="h-3 w-3" />;
      case 'youtube':return <Youtube className="h-3 w-3" />;
      case 'x':return <Twitter className="h-3 w-3" />;
      case 'email':return <Mail className="h-3 w-3" />;
      default:return <Send className="h-3 w-3" />;
    }
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="space-y-6" data-testid="scheduler">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl">
              <CalendarClock className="h-7 w-7 text-white" />
            </div>{t("contentScheduler", "Content Scheduler")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("planAndSchedule", "Plan and schedule your content across all channels")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Eye className="h-4 w-4" />{t("bestTimes", "Best Times")}

          </Button>
          <Button className="gap-2 bg-gradient-to-r from-violet-500 to-purple-600">
            <Plus className="h-4 w-4" />{t("schedulePost", "Schedule Post")}

          </Button>
        </div>
      </div>

      {/* Calendar Card */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-xl font-semibold min-w-[180px] text-center">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant={view === 'month' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('month')}>{t("month", "Month")}


              </Button>
              <Button
                variant={view === 'week' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('week')}>{t("week", "Week")}


              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map((day) =>
            <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                {day}
              </div>
            )}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              const posts = getPostsForDate(day.date);
              return (
                <div
                  key={index}
                  onClick={() => setSelectedDate(day.date)}
                  className={`
                    min-h-[100px] p-2 rounded-lg border transition-colors cursor-pointer
                    ${day.isCurrentMonth ? 'bg-background' : 'bg-muted/30'}
                    ${isToday(day.date) ? 'border-violet-500 bg-violet-50' : 'border-border'}
                    ${selectedDate?.toDateString() === day.date.toDateString() ? 'ring-2 ring-violet-500' : ''}
                    hover:border-violet-300
                  `}>
                  
                  <div className={`text-sm font-medium mb-1 ${!day.isCurrentMonth && 'text-muted-foreground'}`}>
                    {day.date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {posts.slice(0, 2).map((post) =>
                    <div
                      key={post.id}
                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${platformColors[post.platform]}`}>
                      
                        <PlatformIcon platform={post.platform} />
                        <span className="truncate">{post.title}</span>
                      </div>
                    )}
                    {posts.length > 2 &&
                    <div className="text-xs text-muted-foreground px-1.5">
                        +{posts.length - 2}{t("more", "more")}
                    </div>
                    }
                  </div>
                </div>);

            })}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Posts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-violet-600" />{t("upcomingPosts", "Upcoming Posts")}

            </CardTitle>
            <CardDescription>{t("postsScheduledFor", "Posts scheduled for the next 7 days")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {scheduledPosts.
              filter((p) => p.status === 'scheduled').
              sort((a, b) => a.date - b.date).
              slice(0, 5).
              map((post) =>
              <div
                key={post.id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${platformColors[post.platform]}`}>
                        <PlatformIcon platform={post.platform} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{post.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {post.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{t("at", "at")}{post.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200">
                      <CheckCircle className="h-3 w-3 mr-1" />{t("scheduled", "Scheduled")}

                </Badge>
                  </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />{t("draftsPending", "Drafts & Pending")}

            </CardTitle>
            <CardDescription>{t("contentWaitingTo", "Content waiting to be scheduled")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {scheduledPosts.
              filter((p) => p.status === 'draft').
              map((post) =>
              <div
                key={post.id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${platformColors[post.platform]}`}>
                        <PlatformIcon platform={post.platform} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{post.title}</p>
                        <p className="text-xs text-muted-foreground">{t("target", "Target:")}
                      {post.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">{t("schedule", "Schedule")}

                </Button>
                  </div>
              )}
              {scheduledPosts.filter((p) => p.status === 'draft').length === 0 &&
              <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t("noDraftsPending", "No drafts pending")}</p>
                </div>
              }
            </div>
          </CardContent>
        </Card>
      </div>
    </div>);

};

export default Scheduler;